import { useQuery } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/hooks/useAuth';
import type {
  NativePurchaseTarget,
  NativeVipProPlatform,
  NativeVipProTier,
} from '@/services/nativeVipPro';

/**
 * VIP and Pro in the iOS and Android apps (Phase 5). The service module is
 * loaded on demand, so members whose flags are off never download it.
 * Plan: docs/trip/plans/F_2026-10-28_vip-pro-membership.plan.md, section 6.
 */

const loadNativeVipPro = () => import('@/services/nativeVipPro');

/**
 * 'ios' or 'android' in the apps; null on web. A build without store billing
 * still answers its platform: its store quote comes back unavailable, which
 * keeps today's offer.
 */
export function nativeVipProPlatform(): NativeVipProPlatform | null {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android' ? platform : null;
}

type NativeFlag = 'vip_paywall' | 'vip_rescue' | 'pro_checkout';

/**
 * VIP, the rescue and Pro are built into the apps: on in every iOS and Android
 * build, off on web (web reads its own *_web flags). No server value can turn
 * them on or off after App Review (Alex, Oct 31 2026), so the build Apple
 * reviews is the build members get. A store that cannot quote the products
 * still falls back to today's offer through useNativeVipProQuote.
 */
export function useNativeVipFlag(_flag: NativeFlag, enabled = true): { on: boolean; isLoading: boolean } {
  return { on: enabled && nativeVipProPlatform() != null, isLoading: false };
}

/** Store quote for VIP or Pro. `unavailable` (with the reason) means show today's offer. */
export function useNativeVipProQuote(tier: NativeVipProTier, enabled: boolean) {
  const { user } = useAuth();
  const platform = nativeVipProPlatform();
  return useQuery({
    queryKey: ['native-vip-pro-quote', tier, user?.id],
    queryFn: async () => (await loadNativeVipPro()).getNativeVipProQuote(user!.id, tier),
    enabled: enabled && !!user && !!platform,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The cross-store guard for this purchase. Anything but `allowed` hides the buy button. */
export function useNativePurchaseDecision(target: NativePurchaseTarget, enabled: boolean) {
  const { user } = useAuth();
  const platform = nativeVipProPlatform();
  return useQuery({
    queryKey: ['native-vip-pro-decision', target, user?.id],
    queryFn: async () => (await loadNativeVipPro()).checkNativePurchaseAllowed(platform!, target),
    enabled: enabled && !!user && !!platform,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
